import { useEffect, useState } from 'react';
import { EmptyState, TableSkeleton } from '../components/States';
import { useTranslation } from 'react-i18next';
import type { StockByProduct, StockRow } from '@teo/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { Pager } from '../components/Pager';
import { InventoryImportModal } from '../ops/InventoryImportModal';
import { variantLabel } from '../components/Operation';
import { useLoad } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { formatMoney, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

/** Inventario: una fila por referencia y ubicación, con tránsito entrante, alerta de mínimo y detalle por color y talla. */
export function Inventory() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { user, can } = useAuth();
  const { showError } = useToast();
  const seesCost = can('cost.view');
  const [location, setLocation] = useState('');
  const [term, setTerm] = useState('');
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [detail, setDetail] = useState<{ row: StockByProduct; variants: StockRow[] } | null>(null);
  // La búsqueda y la paginación se hacen en el servidor: con miles de referencias por ubicación no se trae todo.
  const [applied, setApplied] = useState('');
  useEffect(() => {
    const next = term.trim();
    if (next === applied) return undefined;
    const id = setTimeout(() => { setApplied(next); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [term, applied]);
  const stock = useLoad(
    () => api.inventoryByProduct({ location_id: location ? Number(location) : undefined, q: applied || undefined, limit: pageSize, offset: (page - 1) * pageSize }),
    [location, applied, page, pageSize],
  );
  const visible = stock.data?.items ?? [];
  const total = stock.data?.total ?? 0;
  const totalUnits = stock.data?.units ?? 0;

  const openDetail = (row: StockByProduct) => {
    api.inventory({ location_id: row.location_id, q: row.ref, limit: 500 })
      .then((variants) => setDetail({ row, variants: variants.filter((v) => v.product_id === row.product_id) }))
      .catch(showError);
  };

  return (
    <Panel
      title={<span id="inventoryTitle">{t('inventory.title')}</span>}
      description={t('inventory.description')}
      badge={can('import.inventory') ? (
        <button type="button" className="btn" id="importInventoryBtn" onClick={() => setImporting(true)}>
          {t('invImport.button')}
        </button>
      ) : null}
    >
      <InventoryImportModal open={importing} defaultMode="sum" onClose={() => setImporting(false)} onImported={() => void stock.reload()} />
      <div className="actions" style={{ marginBottom: 12 }}>
        <input className="input" id="invSearch" style={{ flex: 1, minWidth: 220 }} placeholder={t('inventory.search')} value={term} onChange={(e) => setTerm(e.target.value)} />
        {(user?.locations.length ?? 0) > 1 ? (
          <select className="select" id="invLocation" value={location} onChange={(e) => { setLocation(e.target.value); setPage(1); }}>
            <option value="">{t('inventory.allLocations')}</option>
            {user?.locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <div className="tablewrap">
        <table>
          <thead id="invHead">
            <tr>
              <th>{t('inventory.cols.ref')}</th>
              <th>{t('inventory.cols.product')}</th>
              <th>{t('inventory.cols.location')}</th>
              <th className="num">{t('inventory.cols.qty')}</th>
              <th className="num">{t('inventory.cols.transit')}</th>
              {seesCost ? <th className="num">{t('inventory.cols.value')}</th> : null}
              <th>{t('common.status')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody id="invBody">
              {stock.loading && !stock.data ? <TableSkeleton cols={8} /> : null}
            {visible.map((r) => (
              <tr key={`${r.location_id}-${r.product_id}`} data-ref={r.ref} data-location={r.location_code}>
                <td>
                  <b>{r.ref}</b>
                </td>
                <td>
                  {r.product_name}
                  <br />
                  <span className="tiny muted">{[r.brand, r.category].filter(Boolean).join(' · ')}</span>
                </td>
                <td>{r.location_name}</td>
                <td className="num" data-col="qty">{formatNumber(r.qty, lang)}</td>
                <td className="num" data-col="transit">{r.incoming_transit ? formatNumber(r.incoming_transit, lang) : '—'}</td>
                {seesCost ? <td className="num">{formatMoney(r.value_landed ?? 0, lang)}</td> : null}
                <td>{r.qty <= 0 ? <Badge tone="red">{t('inventory.out')}</Badge> : r.qty < r.min_stock ? <Badge tone="amber">{t('inventory.low')}</Badge> : <Badge tone="green">{t('inventory.ok')}</Badge>}</td>
                <td>
                  {r.stock_granularity !== 'NONE' ? (
                    <button type="button" className="btn sm" onClick={() => openDetail(r)}>
                      {t('inventory.detail')}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
            {stock.data && visible.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty">
                  <EmptyState icon="inventory" title={t('inventory.empty')} hint={t('inventory.emptyHint')} />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <div className="help" id="invCount" style={{ marginTop: 9 }}>
        {t('inventory.count', { refs: formatNumber(total, lang), units: formatNumber(totalUnits, lang) })}
      </div>
      <Pager id="invPager" page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={(n) => { setPageSize(n); setPage(1); }} />

      <Modal open={!!detail} title={detail ? t('ops.refName', { ref: detail.row.ref, name: detail.row.product_name }) : ''} help={detail?.row.location_name} onClose={() => setDetail(null)}>
        {detail ? (
          <div className="tablewrap" id="invDetail">
            <table>
              <thead>
                <tr>
                  <th>{t('inventory.cols.variant')}</th>
                  <th>{t('inventory.cols.sku')}</th>
                  <th>{t('inventory.cols.qty')}</th>
                </tr>
              </thead>
              <tbody>
                {detail.variants.map((v) => (
                  <tr key={v.variant_id}>
                    <td>{variantLabel(v) || '—'}</td>
                    <td className="tiny">{v.sku}</td>
                    <td>{formatNumber(v.qty, lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Modal>
    </Panel>
  );
}
